import { Box, Paper, alpha } from '@mui/material';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import { Theme } from '@mui/material/styles';
import * as React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { makeStyles } from 'tss-react/mui';
import { ResourceType } from '../../modules/users/ability.types';
import { SettingsMenuTestIds } from '../../utils/testIds/SettingsMenu.testIds';
import useWidth from '../Hooks/useWidth';
import NewFeatureChip from '../chips/NewFeatureChip';

import ListItemButton from '@mui/material/ListItemButton';

const useStyles = makeStyles()((theme: Theme) => ({
  menuItemButton: {
    borderRadius: theme.spacing(3),
    textTransform: 'lowercase',
    minHeight: '32px',
  },
  menuColor: {
    color: theme.palette.text.primary,
  },
  menuSelected: {
    color: theme.palette.primary.main,
    '&:hover': {
      backgroundColor: alpha(theme.palette.primary.main, theme.palette.action.hoverOpacity),
    },
    '& .MuiTypography-body2': {
      fontWeight: theme.typography.fontWeightBold,
    },
  },
  menuTitle: {
    paddingTop: theme.spacing(2),
  },
  itemsDistance: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  titleFontSize: {
    fontSize: '13px',
  },
  mobileButton: {
    paddingTop: '8px',
    paddingBottom: '8px',
  },
}));

export interface MenuItem {
  name: ResourceType;
  label: string;
  pathname: string;
  active?: boolean;
  isNewFeature?: boolean;
}

export interface CustomMenuItemProps {
  group: boolean;
  label: string;
  items: MenuItem[];
}

type CustomMenuProps = {
  items: CustomMenuItemProps[];
};

const filterItem = (item: any, ability: any) => {
  if (item.group) {
    const children = item.items.map((sub: any) => filterItem(sub, ability)).filter((i: any) => !!i);
    if (children && children.length > 0) {
      return {
        ...item,
        items: children,
      };
    }
    return null;
  }

  if (ability.can('menu', item.name)) {
    return item;
  }
  return null;
};

export const createMenu = (menuItems: any, ability: any) => {
  const menus: any = [];
  menuItems.forEach((item: any) => {
    const menuItem = filterItem(item, ability);
    if (menuItem) {
      menus.push(menuItem);
    }
  });
  return menus;
};

interface RenderListItemProps {
  item: MenuItem;
  isSubMenu?: boolean;
}

const RenderListItem: React.FC<RenderListItemProps> = ({ item, isSubMenu }) => {
  const location = useLocation();
  const { classes, cx } = useStyles();
  const { mobileWidth } = useWidth();
  const paths = location.pathname.split('/');
  const active =
    `/${paths[1]}/${paths[2]}` === item.pathname.split('?')[0] ||
    location.pathname === item.pathname;

  return (
    <React.Fragment key={item.label}>
      <ListItemButton
        className={cx({
          [classes.menuSelected]: isSubMenu && active,
          [classes.mobileButton]: mobileWidth,
        })}
        classes={{ root: classes.menuItemButton }}
        component={Link}
        to={item.pathname}
        {...{ 'test-id': `${SettingsMenuTestIds.MENU_ITEM_PREFIX}${item.name}` }}
      >
        <ListItemText primaryTypographyProps={{ variant: 'subtitle1' }} primary={item.label} />
        {item.isNewFeature && <NewFeatureChip />}
      </ListItemButton>
    </React.Fragment>
  );
};

interface RenderItemProps {
  item: CustomMenuItemProps;
  index: number;
}

const RenderItem: React.FC<RenderItemProps> = ({ item, index }) => {
  const { classes } = useStyles();

  if (item.group) {
    return (
      <React.Fragment key={item.label}>
        <ListItem>
          <ListItemText
            primaryTypographyProps={{ variant: 'subtitle2' }}
            primary={item.label}
            classes={{
              root: index === 0 ? '' : classes.menuTitle,
              primary: classes.titleFontSize,
            }}
          />
        </ListItem>
        <List component="div" disablePadding dense classes={{ root: classes.itemsDistance }}>
          {item.items.map((menuItem: MenuItem) => (
            <RenderListItem key={menuItem.label} item={menuItem} isSubMenu={true} />
          ))}
        </List>
      </React.Fragment>
    );
  }

  return <RenderListItem item={item as any} />;
};

const CustomMenu: React.FC<any> = ({ items }: CustomMenuProps) => {
  const { mobileWidth } = useWidth();

  if (mobileWidth) {
    return (
      <Box
        sx={{
          paddingBottom: 8,
          padding: 1,
        }}
      >
        <Paper elevation={0}>
          <List dense disablePadding>
            {items.map((item, index) => (
              <RenderItem key={item.label} item={item} index={index} />
            ))}
          </List>
        </Paper>
      </Box>
    );
  }

  return (
    <Paper elevation={0}>
      <Box
        sx={{
          p: 1,
        }}
      >
        <List dense disablePadding>
          {items.map((item, index) => (
            <RenderItem key={item.label} item={item} index={index} />
          ))}
        </List>
      </Box>
    </Paper>
  );
};

export default CustomMenu;
